/**
 * Where an app is in its life, and the one thing standing in its way.
 *
 * The console has always held these facts and never stated them. Whether an app
 * is designed, registered, being tested or actually used is spread across a spec
 * version, a DLR, a test-user list and an authorization count — four places, none
 * of which is a state, so nobody could see at a glance which of their apps needed
 * them. This derives the state, and derives the ONE thing worth doing about it.
 *
 * It is a pure function on purpose. The home worklist and an app's own header must
 * say the same thing about the same app, and the only way to guarantee that is for
 * both to call this rather than each reading the raw fields their own way.
 *
 * NOTHING HERE ISSUES A REQUEST. Callers pass in what they already fetched. The
 * three fleet endpoints (`my-apps`, `my-apps/overview`, `my-apps/scores`) between
 * them carry everything below except the draft, which is per-app.
 */

/**
 * The ladder. `rank` orders the fleet — least settled first, because an app you
 * have not finished is more likely to be why you opened the page than one that
 * has been serving users for a year.
 *
 *   Designing  — no design document (CRUX) yet. Every app gets a client_id when
 *                its record is made, so "registered" alone can never be the
 *                first rung; the document is what a new app is still missing.
 *   Registered — designed and on record; nobody has authorized it yet, or we do
 *                not know yet (a lower bound, never a claim of zero).
 *   Testing    — authorized, but every authorization may be a test user's.
 *   Live       — at least one real (non-test) person has authorized it.
 *   Charging   — live, priced, AND the payout account can be paid into.
 *
 * "Charging" rather than "Earning": the price is a fact we hold on the app record;
 * revenue is not, and would need a per-app billing call to claim. A state named
 * after money that has not been counted is a claim the page cannot back — and a
 * priced app with no payout account cannot take a payment at all, so it stays
 * Live and the blocker says why.
 */
export const STATES = {
  designing: { key: 'designing', label: 'Designing', rank: 0 },
  registered: { key: 'registered', label: 'Registered', rank: 1 },
  testing: { key: 'testing', label: 'Testing', rank: 2 },
  live: { key: 'live', label: 'Live', rank: 3 },
  charging: { key: 'charging', label: 'Charging', rank: 4 },
};

function isPaid(pricing) {
  const model = pricing && pricing.model;
  return !!model && model !== 'free';
}

/**
 * An app that asks for nothing is not necessarily broken — an OPEN DLR requests
 * nothing and accepts whatever the user grants, and it is the one legitimate case
 * of an empty request list. Treating every empty list as a fault would flag those
 * apps forever with nothing to fix.
 */
function asksForNothing(app) {
  const inputs = app.dlr_inputs || [];
  if (inputs.length > 0) return false;
  const dlr = app.data_lender_request || {};
  if (dlr.open === true || dlr.is_open === true) return false;
  return true;
}

/**
 * Has this app ever been pushed? `spec_version` starts at 1 for a registered app,
 * so it cannot distinguish "registered and never changed" from "never registered".
 * A client_id can: it is assigned at registration and at no other time.
 */
function isRegistered(app) {
  return !!app.client_id;
}

/**
 * Does the app have a design document (CRUX) — true, false, or undefined when
 * nothing we hold can say. The app record carries `crux` ({sections: {...}},
 * or null); the backend's own test (`_has_crux`) is "any section has text", so
 * this is the same test. Without the field, the scores row's `blocked: 'no_crux'`
 * is the next best witness. A staged CRUX does not count until it is committed.
 */
export function hasCrux(app, score) {
  if (app && 'crux' in app) {
    const sections = app.crux && typeof app.crux === 'object' ? app.crux.sections : null;
    return !!sections && Object.values(sections).some((v) => String(v ?? '').trim() !== '');
  }
  if (score && 'blocked' in score) return score.blocked !== 'no_crux';
  return undefined;
}

/** A count we were given, or undefined when we were not — never a silent zero. */
function known(n) {
  return n === null || n === undefined || Number.isNaN(Number(n)) ? undefined : Number(n);
}

/**
 * How many REAL (non-test) people have authorized the app, as far as we can
 * tell. `facts.realUsers` is the overview's exact `real_users_count` (live
 * grants, test accounts left out the way /authorized-users leaves them out) —
 * every page reads that one number, so Home, the Apps list and the app's own
 * page put an app in the same stage. Only an older backend without the field
 * falls back to the overview's count minus the app's test users: a lower bound
 * that can under-count (a test user who revoked) but never calls an app live
 * on the strength of its own test accounts.
 */
export function realUsersAtLeast(app, facts = {}) {
  const exact = known(facts.realUsers);
  if (exact !== undefined) return exact;
  const authorized = known(facts.authorizedUsers);
  if (authorized === undefined) return undefined;
  return Math.max(0, authorized - (app.test_user_ids || []).length);
}

/**
 * The lifecycle state, from facts the caller already has.
 *
 * Unknown facts are unknown, not zero: before the counts load (or if they
 * fail) an app sits at the lowest rung its record proves, and no rung above it
 * is claimed.
 *
 * @param {object} app    a row from `/third-party/my-apps`
 * @param {object} facts
 * @param {number} [facts.authorizedUsers]  from `/third-party/my-apps/overview` (includes test users)
 * @param {number} [facts.realUsers]        exact non-test authorizations (overview `real_users_count`)
 * @param {object} [facts.score]            a row from `/third-party/my-apps/scores`
 * @param {boolean} [facts.payoutReady]     the payout account can be paid into
 * @returns {{key: string, label: string, rank: number}}
 */
export function deriveState(app, facts = {}) {
  if (!isRegistered(app)) return STATES.designing;

  // A plugin is executed, never authorized, so the consent-shaped states below
  // would leave every plugin stuck at "registered" forever. Reaching it at all
  // is what "live" means for one.
  if (app.is_plugin) {
    return app.deployment_mode === 'public' ? STATES.live : STATES.registered;
  }

  if (hasCrux(app, facts.score) === false) return STATES.designing;

  const authorized = known(facts.authorizedUsers);
  const real = realUsersAtLeast(app, facts);

  if (real !== undefined && real > 0) {
    return isPaid(app.pricing) && facts.payoutReady === true ? STATES.charging : STATES.live;
  }
  // Somebody authorized, and every one of them may be a test user.
  if (authorized !== undefined && authorized > 0) return STATES.testing;
  return STATES.registered;
}

/**
 * The one thing worth doing about this app, or null.
 *
 * Ordered by what blocks what. A staged change outranks a failing eval because
 * the eval is measured against the live spec, and committing may well be what
 * fixes it — telling somebody to go fix an eval while their fix is sitting
 * uncommitted is advice that makes the page look like it is not paying attention.
 *
 * @param {object} app
 * @param {object} facts
 * @param {object} [facts.draft]            from `/third-party/<id>/draft`
 * @param {object} [facts.score]            a row from `/third-party/my-apps/scores`
 * @param {number} [facts.authorizedUsers] undefined = not known yet, never read as zero
 * @param {boolean} [facts.payoutReady]     developer-level, from `/billing/payout-account`
 * @returns {{code: string, text: string, action: string, panel: ?string, href: ?string}|null}
 */
export function deriveBlocking(app, facts = {}) {
  const { draft, score, payoutReady } = facts;
  const authorized = known(facts.authorizedUsers);
  const version = app.spec_version || 1;

  if (!isRegistered(app)) {
    return {
      code: 'unregistered',
      text: 'Not registered yet — no app record to build against',
      action: 'Pick it up',
      panel: null,
      href: '/create?lane=continue',
    };
  }

  if (draft && draft.has_changes) {
    const n = draft.count || (draft.changes || []).length;
    return {
      code: 'staged',
      text: `${n} change${n === 1 ? '' : 's'} staged since v${version} — not live yet`,
      action: 'Review & commit',
      // Overview, where the commit bar always stands (it shows on every page
      // while something is staged, but Overview is the page that owns it).
      panel: 'overview',
      href: null,
    };
  }

  // No design document: none of the three scores mean anything (the scores
  // endpoint says `blocked: 'no_crux'`), and the app is still Designing. It is
  // not a failure; it is the absence of the thing failure would be measured
  // against.
  // A plugin's lifecycle does not wait on a CRUX (deriveState routes it around
  // Designing), so for one only the scores row's own word counts, as before.
  const noCrux = app.is_plugin ? !!score && score.blocked === 'no_crux' : hasCrux(app, score) === false;
  if (noCrux) {
    return {
      code: 'no_crux',
      text: 'No design document — nothing can be evaluated',
      action: 'Write the design doc',
      panel: 'crux',
      href: null,
    };
  }

  if (asksForNothing(app)) {
    return {
      code: 'no_dlr',
      text: 'Asks for nothing — there is no consent screen to show',
      action: 'Declare access',
      panel: 'access',
      href: null,
    };
  }

  // `not_applicable` is carried separately by the endpoint for a reason: a check
  // that could not run is not a check that passed, and folding it into either side
  // of the fraction would misreport the app in one direction or the other.
  if (score && score.checks && score.checks.total) {
    const failing = score.checks.total - score.checks.passed;
    if (failing > 0) {
      return {
        code: 'checks_failing',
        text: `${failing} of ${score.checks.total} evals failing`,
        action: 'See the evals',
        // The app page's OS-Evals tab is `readiness` (MyAppsTab tabGroups);
        // 'evals' named no tab, so this blocker landed on Details.
        panel: 'readiness',
        href: null,
      };
    }
  }

  if (isPaid(app.pricing) && payoutReady === false) {
    return {
      code: 'no_payout',
      text: 'Priced, but payouts are not ready — nobody can buy it yet',
      action: 'Set up payouts',
      panel: null,
      href: '/billing',
    };
  }

  // Only on a count we actually have. Before the overview loads, or when it
  // fails, "nobody has authorized it" would be a claim made from nothing.
  if (!app.is_plugin && authorized === 0) {
    return {
      code: 'no_users',
      text: 'Nobody has authorized it — not even a test user',
      action: 'Make a test user',
      panel: 'testusers',
      href: null,
    };
  }

  return null;
}

/**
 * Both, plus a sort key — the shape the worklist renders from.
 *
 * Apps with something blocking them sort above apps without, and within each
 * band the least settled comes first. An app that needs nothing is still listed:
 * a fleet view that hides what is healthy stops being a fleet view.
 */
export function describeApp(app, facts = {}) {
  const state = deriveState(app, facts);
  const blocking = deriveBlocking(app, facts);
  return {
    id: app.id,
    name: app.name,
    isPlugin: !!app.is_plugin,
    version: app.spec_version || 1,
    registered: isRegistered(app),
    state,
    blocking,
    sortKey: [blocking ? 0 : 1, state.rank, (app.name || '').toLowerCase()],
  };
}

/** Least settled and most blocked first; stable by name inside a band. */
export function byUrgency(a, b) {
  for (let i = 0; i < a.sortKey.length; i += 1) {
    if (a.sortKey[i] < b.sortKey[i]) return -1;
    if (a.sortKey[i] > b.sortKey[i]) return 1;
  }
  return 0;
}

/**
 * Which of the app's pages hold a staged change, from the draft's field list —
 * so the page list can mark where the uncommitted work is. Anything not named
 * here is an app-record field and lives on Overview: the default has to be the
 * catch-all, because a field added to the backend's STAGEABLE set must not
 * silently lose its marker.
 */
const STAGED_FIELD_PAGE = {
  yaml: 'access', dlr: 'access', input_parameters: 'access',
  pricing: 'pricing',
  crux: 'crux',
};

export function stagedPages(draft) {
  const pages = new Set();
  if (!draft || !draft.has_changes) return pages;
  for (const c of draft.changes || []) pages.add(STAGED_FIELD_PAGE[c.field] || 'overview');
  return pages;
}
